import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    patientAddSubDocument,
    patientRemoveSubDocument,
    patientUpdateSubDocument,
    patientSetSubDocument,
} from '../../../service/api/patient-api';
import MedicationListPanel from '../medication-list/medication-list-panel';
import { resolveActingDoctorId } from '../../../utils/common-funtions/common';
import './current-medication.scss';

// One constant, never the bare string: patient.currentMedications (the list) and
// visit.currentMedication (the legacy text) differ by a single character, and a
// typo in a $set key is silently dropped by Mongoose and still returns 200.
export const CURRENT_MEDICATION_FIELD = 'currentMedications';

// Current Medication is stored on the PATIENT, like Past Medication, so the list
// carries from visit to visit without being re-entered. `visitDetails` is only read
// for the acting doctor and for the legacy per-visit free text shown above the list.
const CurrentMedication = ({ visitDetails, patientDetails, updateState, patientId, headerSlot }) => {
    const { user, selectedRole, selectedDoctor } = useSelector((state) => state.auth);
    const actingDoctorId = visitDetails?.doctor || resolveActingDoctorId({ user, selectedRole, selectedDoctor });

    const [medicines, setMedicines] = useState([]);

    useEffect(() => {
        setMedicines(patientDetails?.[CURRENT_MEDICATION_FIELD] || []);
    }, [patientDetails]);

    const applyPatient = (patient) => {
        setMedicines(patient?.[CURRENT_MEDICATION_FIELD] || []);
        if (updateState) updateState(patient);
    };

    const addMedicine = (payLoad) =>
        patientAddSubDocument(CURRENT_MEDICATION_FIELD, payLoad, patientId)
            .then((res) => { if (res?.status === 200) applyPatient(res?.data); })
            .catch((error) => { console.log(error); });

    const deleteMedicine = (medicine) =>
        patientRemoveSubDocument(CURRENT_MEDICATION_FIELD, { _id: medicine?._id }, patientId)
            .then((res) => { if (res?.status === 200) applyPatient(res?.data); })
            .catch((error) => { console.log(error); });

    const updateField = (value, type, id) =>
        patientUpdateSubDocument({
            filterKey: `${CURRENT_MEDICATION_FIELD}._id`,
            filterKeyValue: id,
            payload: { [`${CURRENT_MEDICATION_FIELD}.$.${type}`]: value },
        }, patientId)
            .then((res) => { if (res?.status === 200) applyPatient(res?.data); })
            .catch((error) => { console.log(error); });

    const reorder = (sortedList) => {
        setMedicines(sortedList);
        patientSetSubDocument(CURRENT_MEDICATION_FIELD, sortedList, patientId)
            .then((res) => { if (res?.status === 200) applyPatient(res?.data); })
            .catch(() => { });
    };

    // Whatever was typed into the old per-visit free-text box stays visible and
    // untouched. This is the only thing here that is still visit-scoped.
    const legacyLines = (visitDetails?.currentMedication || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    const legacySlot = legacyLines.length ? (
        <div className="cm-legacy">
            <div className="cm-legacy__head">This visit, entered as text (read-only)</div>
            {legacyLines.map((line, index) => (
                <p className="cm-legacy__line" key={index}>• {line}</p>
            ))}
        </div>
    ) : null;

    return (
        <MedicationListPanel
            title="Current Medication"
            fieldKey={CURRENT_MEDICATION_FIELD}
            items={medicines}
            doctorId={actingDoctorId}
            visitId={visitDetails?._id}
            emptyText="No current medications added yet"
            // The sig form's edit path writes to the visit, but this list lives on the
            // patient — same reason it stays closed for Past Medication.
            enableSigEdit={false}
            headerSlot={headerSlot}
            listTopSlot={legacySlot}
            onAdd={addMedicine}
            onDelete={deleteMedicine}
            onUpdateField={updateField}
            onReorder={reorder}
        />
    );
};

export default CurrentMedication;
